/**
 * useStorageUpload — uploads files to Supabase Storage bucket "article-images".
 * Returns the public URL after upload.
 * No mock fallback. Real errors surfaced.
 */
import { useCallback, useState } from "react";
import { supabase } from "@/lib/supabase";

export interface UploadResult {
  url: string | null;
  error: string | null;
}

export interface UploadProgress {
  uploading: boolean;
  progress: number; // 0-100
  fileName: string | null;
}

const BUCKET = "article-images";

function sanitizeFileName(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9.\-_]/g, "-")
    .replace(/-+/g, "-");
}

function buildStoragePath(file: File): string {
  const ts = Date.now();
  const safe = sanitizeFileName(file.name);
  return `uploads/${ts}-${safe}`;
}

export function useStorageUpload() {
  const [progress, setProgress] = useState<UploadProgress>({
    uploading: false,
    progress: 0,
    fileName: null,
  });

  /**
   * Upload a single File to Supabase Storage.
   * Returns { url, error }.
   */
  const uploadFile = useCallback(async (file: File): Promise<UploadResult> => {
    setProgress({ uploading: true, progress: 0, fileName: file.name });

    const path = buildStoragePath(file);

    const { error: uploadError } = await supabase.storage
      .from(BUCKET)
      .upload(path, file, {
        cacheControl: "3600",
        upsert: false,
        contentType: file.type,
      });

    if (uploadError) {
      setProgress({ uploading: false, progress: 0, fileName: null });
      return {
        url: null,
        error: uploadError.message,
      };
    }

    const { data: urlData } = supabase.storage.from(BUCKET).getPublicUrl(path);
    const publicUrl = urlData?.publicUrl ?? null;

    setProgress({ uploading: false, progress: 100, fileName: null });

    if (!publicUrl) {
      return { url: null, error: "Konnte keine öffentliche URL abrufen." };
    }

    return { url: publicUrl, error: null };
  }, []);

  /**
   * Upload multiple files sequentially.
   * Returns array of results in same order as input.
   */
  const uploadFiles = useCallback(
    async (files: File[]): Promise<UploadResult[]> => {
      const results: UploadResult[] = [];
      for (let i = 0; i < files.length; i++) {
        setProgress({
          uploading: true,
          progress: Math.round((i / files.length) * 100),
          fileName: files[i].name,
        });
        const result = await uploadFile(files[i]);
        results.push(result);
      }
      setProgress({ uploading: false, progress: 0, fileName: null });
      return results;
    },
    [uploadFile]
  );

  return { uploadFile, uploadFiles, progress };
}
